/* KBW request forms -> the admin app's request inbox.

   The storefront's enquiry forms (the free design request in
   `sections/kbw-request-form.liquid`, the contact page in
   `sections/contact-form.liquid`) used to be Shopify's own `contact` form.
   When a section's "Send submissions to" setting is left on the app, the form
   is instead a plain multipart <form> that posts to the app proxy endpoint
   `/apps/kbw-admin/request`. This file is what submits it: the same pattern as
   `assets/kbw-buyer-program.js` (fetch, no page reload, the page's own success
   and error boxes, Turnstile reset after a failure), shared by every request
   form so it is written once.

   The endpoint answers either with JSON (asked for with the Accept header) or
   with a redirect back to the page carrying `?request=sent` or
   `?request=error&reason=...`. Both are understood here. The redirect is also
   what a visitor without JavaScript gets -- the form's own action/method is a
   real POST to the same endpoint -- so on load this file reads those two
   parameters, shows the matching box, and takes them off the address so a
   reload does not announce the same result twice.

   Markup contract:
     [data-kbw-request-form]        the form; data-endpoint = URL to post to,
                                    data-error-message = the section's own
                                    general error sentence, and
                                    data-error-<reason> (reason with dashes:
                                    invalid, verification, too-large,
                                    too-many-files, file-type, rate-limited,
                                    server) = its sentence for that reason
     [data-kbw-request-ok]          success box, hidden until it succeeded
     [data-kbw-request-err]         error box, hidden until something failed
     [data-kbw-request-err-text]    where the error sentence is written
     [data-kbw-request-submit]      the submit button (optional
                                    data-sending-label = text while sending)
     [data-kbw-request-turnstile]   Cloudflare Turnstile's container
     [data-kbw-request-files]       wrapper of the file field; data-max-files,
                                    data-max-mb
     [data-kbw-request-files-input] the <input type="file" multiple>
     [data-kbw-request-files-trigger] "Choose files" button, shown by this
                                    script (without it the native input is
                                    what the visitor sees and uses)
     [data-kbw-request-files-list]  the chosen files row
     [data-kbw-request-files-names] where the chosen file names are written
     [data-kbw-request-files-clear] clears the chosen files
     [data-kbw-request-files-error] the field's own error line
*/

(function () {
  'use strict';

  var ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
  var ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

  /* The app's own total cap for one submission (docs/requests-endpoint.md in
     the admin app). Per-file size and count come from the block's settings. */
  var MAX_TOTAL_BYTES = 25 * 1024 * 1024;

  var NETWORK_ERROR_MESSAGE = 'We could not reach the server. Please check your connection and try again.';
  var GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

  function q(root, name) {
    return root.querySelector('[' + name + ']');
  }

  function fileTypeIsAllowed(file) {
    if (file.type && ALLOWED_TYPES.indexOf(file.type) !== -1) return true;
    var name = (file.name || '').toLowerCase();
    for (var i = 0; i < ALLOWED_EXTENSIONS.length; i++) {
      var ext = ALLOWED_EXTENSIONS[i];
      if (name.slice(name.length - ext.length) === ext) return true;
    }
    return false;
  }

  /* ---------- File field ---------- */

  function showFilesError(wrap, message) {
    var el = q(wrap, 'data-kbw-request-files-error');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  function hideFilesError(wrap) {
    var el = q(wrap, 'data-kbw-request-files-error');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  function renderFileNames(wrap) {
    var input = q(wrap, 'data-kbw-request-files-input');
    var list = q(wrap, 'data-kbw-request-files-list');
    var names = q(wrap, 'data-kbw-request-files-names');
    var trigger = q(wrap, 'data-kbw-request-files-trigger');
    var files = input && input.files ? input.files : [];
    var picked = [];
    for (var i = 0; i < files.length; i++) picked.push(files[i].name);

    if (names) names.textContent = picked.join(', ');
    if (list) list.hidden = picked.length === 0;
    /* The upload itself only happens on submit, so a spinner here would be a
       lie. What the shopper needs is confirmation that the file was taken:
       the wrapper carries the state and the stylesheet draws the tick. */
    wrap.classList.toggle('is-picked', picked.length > 0);
    if (trigger) {
      trigger.textContent = picked.length
        ? trigger.getAttribute('data-change-label') || trigger.textContent
        : trigger.getAttribute('data-choose-label') || trigger.textContent;
    }
  }

  function clearFiles(wrap) {
    var input = q(wrap, 'data-kbw-request-files-input');
    if (input) input.value = '';
    renderFileNames(wrap);
  }

  function setUpFileField(wrap) {
    var input = q(wrap, 'data-kbw-request-files-input');
    var trigger = q(wrap, 'data-kbw-request-files-trigger');
    var clearButton = q(wrap, 'data-kbw-request-files-clear');
    if (!input) return;

    var maxFiles = parseInt(wrap.getAttribute('data-max-files'), 10) || 5;
    var maxMb = parseInt(wrap.getAttribute('data-max-mb'), 10) || 10;

    /* Only now that the script is running is the styled button worth showing;
       the native input then steps back but stays focusable through its label. */
    wrap.classList.add('is-enhanced');
    input.tabIndex = -1;
    if (trigger) {
      trigger.hidden = false;
      trigger.addEventListener('click', function () {
        input.click();
      });
    }

    input.addEventListener('change', function () {
      hideFilesError(wrap);
      var files = input.files || [];

      if (files.length > maxFiles) {
        showFilesError(wrap, 'Please choose up to ' + maxFiles + (maxFiles === 1 ? ' file.' : ' files.'));
        clearFiles(wrap);
        return;
      }
      var total = 0;
      for (var i = 0; i < files.length; i++) {
        total += files[i].size;
        if (!fileTypeIsAllowed(files[i])) {
          showFilesError(wrap, files[i].name + ' is not a PDF, JPG or PNG file.');
          clearFiles(wrap);
          return;
        }
        if (files[i].size > maxMb * 1024 * 1024) {
          showFilesError(wrap, files[i].name + ' is larger than ' + maxMb + ' MB.');
          clearFiles(wrap);
          return;
        }
      }
      if (total > MAX_TOTAL_BYTES) {
        showFilesError(wrap, 'These files come to more than 25 MB together. Please send fewer or smaller files.');
        clearFiles(wrap);
        return;
      }
      renderFileNames(wrap);
    });

    if (clearButton) {
      clearButton.addEventListener('click', function () {
        hideFilesError(wrap);
        clearFiles(wrap);
        if (trigger) trigger.focus();
      });
    }
  }

  /* ---------- Messages ---------- */

  function showOk(form) {
    var ok = q(form, 'data-kbw-request-ok');
    var err = q(form, 'data-kbw-request-err');
    if (err) err.hidden = true;
    if (ok) {
      ok.hidden = false;
      ok.focus();
    }
  }

  function showError(form, message) {
    var ok = q(form, 'data-kbw-request-ok');
    var err = q(form, 'data-kbw-request-err');
    var text = q(form, 'data-kbw-request-err-text');
    if (ok) ok.hidden = true;
    if (text) text.textContent = message;
    if (err) {
      err.hidden = false;
      err.focus();
    }
  }

  function hideMessages(form) {
    var ok = q(form, 'data-kbw-request-ok');
    var err = q(form, 'data-kbw-request-err');
    if (ok) ok.hidden = true;
    if (err) err.hidden = true;
  }

  /* The section's own sentence for this reason, else the server's sentence,
     else the section's general one. */
  function errorFor(form, reason, serverSentence) {
    if (reason && /^[a-z_]+$/.test(reason)) {
      var own = form.getAttribute('data-error-' + reason.replace(/_/g, '-'));
      if (own) return own;
    }
    return serverSentence || form.getAttribute('data-error-message') || GENERIC_ERROR_MESSAGE;
  }

  function resetTurnstile(form) {
    var widget = q(form, 'data-kbw-request-turnstile');
    if (widget && window.turnstile && typeof window.turnstile.reset === 'function') {
      window.turnstile.reset(widget);
    }
  }

  function resetAll(form) {
    form.reset();
    var wraps = form.querySelectorAll('[data-kbw-request-files]');
    for (var i = 0; i < wraps.length; i++) {
      hideFilesError(wraps[i]);
      clearFiles(wraps[i]);
    }
    resetTurnstile(form);
  }

  /* The outcome of a response, whichever way the app chose to answer:
     JSON { ok, error }, or a redirect whose final URL carries ?request=. */
  function readOutcome(response) {
    var type = response.headers.get('content-type') || '';
    if (type.indexOf('application/json') !== -1) {
      return response
        .json()
        .catch(function () {
          return null;
        })
        .then(function (json) {
          if (json && json.ok) return { ok: true };
          return { ok: false, reason: json && json.reason, message: json && json.error };
        });
    }
    var status = null;
    var reason = null;
    try {
      var params = new URL(response.url, window.location.href).searchParams;
      status = params.get('request');
      reason = params.get('reason');
    } catch (e) {
      status = null;
    }
    if (status === 'sent') return Promise.resolve({ ok: true });
    return Promise.resolve({ ok: false, reason: reason });
  }

  /* ---------- Submit ---------- */

  function setUpForm(form) {
    var endpoint = form.getAttribute('data-endpoint');
    var button = q(form, 'data-kbw-request-submit');
    var buttonHtml = button ? button.innerHTML : '';
    var sendingLabel = button ? button.getAttribute('data-sending-label') : '';
    var submitting = false;

    var wraps = form.querySelectorAll('[data-kbw-request-files]');
    for (var i = 0; i < wraps.length; i++) setUpFileField(wraps[i]);

    form.addEventListener('submit', function (event) {
      if (!endpoint || !window.fetch || !window.FormData) return; // plain POST instead
      event.preventDefault();
      if (submitting) return;

      submitting = true;
      hideMessages(form);
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (sendingLabel) button.textContent = sendingLabel;
      }

      fetch(endpoint, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
        .then(readOutcome)
        .then(function (outcome) {
          if (outcome.ok) {
            resetAll(form);
            showOk(form);
          } else {
            showError(form, errorFor(form, outcome.reason, outcome.message));
            resetTurnstile(form);
          }
        })
        .catch(function () {
          showError(form, NETWORK_ERROR_MESSAGE);
          resetTurnstile(form);
        })
        .then(function () {
          submitting = false;
          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
            button.innerHTML = buttonHtml;
          }
        });
    });
  }

  /* ---------- The answer a redirect brought back ---------- */

  function applyRedirectOutcome(forms) {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    var status = params.get('request');
    if (status !== 'sent' && status !== 'error') return;

    var form = forms[0];
    if (!form) return;
    if (status === 'sent') {
      showOk(form);
    } else {
      showError(form, errorFor(form, params.get('reason')));
    }
    var box = q(form, status === 'sent' ? 'data-kbw-request-ok' : 'data-kbw-request-err');
    if (box && box.scrollIntoView) box.scrollIntoView({ block: 'center' });

    params.delete('request');
    params.delete('reason');
    if (window.history && window.history.replaceState) {
      var rest = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + (rest ? '?' + rest : '') + window.location.hash
      );
    }
  }

  var forms = document.querySelectorAll('[data-kbw-request-form]');
  for (var f = 0; f < forms.length; f++) setUpForm(forms[f]);
  applyRedirectOutcome(forms);
})();
